import { Link, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import { addNotification } from "../../stores/notification-store"
import { UpdateComponentConfig } from "../../utils/api"

import useComponentConfigData, { componentConfigData } from "../../hooks/useComponentConfigData.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"
// import useDeviceSchemas, { deviceSchemas } from "../../hooks/useDeviceSchemas.js"
import useUtgData from "../../hooks/useUtgData";

export default function DeviceList() {

    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, componentId } = useParams()
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()
    const { manifest } = useSchemaManifest()

    const { utg, loading: utgLoading, error: utgError } = useUtgData(beId, componentId, config, accessToken)
    const { loading: componentConfigLoading } = useComponentConfigData(beId, utg.value, config, accessToken)
    const devices = useComputed(() => {
        // devices is a subset of componentConfig
        if (componentConfigData.value.componentConfig) {
            return componentConfigData.value.componentConfig?.config?.deviceConfig?.device ?? []
        }
        return []
    })

    // const { loading: deviceSchemaLoading } = useDeviceSchemas(config, accessToken)
    
    const showDeleteModal = useSignal(false)
    const confirmDeviceType = useSignal('')
    const confirmTaskDesc = useSignal('')
    const accordionData = [
        { name: 'Devices', data: devices },
        { name: 'UTG Data', data: utg },
        { name: 'Component Config Data', data: componentConfigData },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifest }
    ]

    function DeviceTableRows({ devices }) {

        if (devices && devices.length == 0) {
            return (
                <tbody><tr><td colSpan="4" className="text-center">This UTG does not contain any configured devices</td></tr></tbody>
            )
        }

        let rows = []
        devices.forEach(device => {
            rows.push(
                <DeviceRow device={device} key={`${device.deviceType}${device.taskDesc}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function DeviceRow({ device }) {
        let deviceType = device.deviceType
        let taskDesc = device.taskDesc
        let encodedDeviceType = encodeURIComponent(deviceType)
        return (
            <tr>
                <td><Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>{device.deviceType}</Link></td>
                <td>{device.taskDesc}</td>
                <td>{device.threadType}</td>
                <td>
                    <Link to={`./${encodedDeviceType}?desc=${taskDesc}`}><Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>{' '}
                    <Button variant="outline-danger" onClick={() => showModal(deviceType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>
                </td>
            </tr>
        )
    }

    function deleteDevice() {
        showDeleteModal.value = false

        // filter out device to be deleted
        const filteredDevices = componentConfigData.value.componentConfig.config.deviceConfig.device.filter(device =>
            !(device.deviceType == confirmDeviceType.value && device.taskDesc == confirmTaskDesc.value));

        // set newly filtered device array in componentConfigData    
        componentConfigData.value.componentConfig.config.deviceConfig.device = filteredDevices

        //build data object for updateComponentConfig request
        let data = {
            config: componentConfigData.value.componentConfig.config,
            hash: componentConfigData.value.componentConfig.hash,
            isCommitted: componentConfigData.value.componentConfig.isCommitted,
            lastUpdatedAt: componentConfigData.value.componentConfig.lastUpdatedAt,
            lastUpdatedBy: componentConfigData.value.componentConfig.lastUpdatedBy,
            revisionId: componentConfigData.value.componentConfig.revisionId,
            componentId: componentConfigData.value.componentConfig.componentId
        }

        console.log(data)
        //updates componentConfig with device removed
        UpdateComponentConfig(beId, utg.value.MachinePermanentId, utg.value.ComponentPermanentId, data, config, accessToken)
            .then(result => {
                console.log('successfully updated component config')
                addNotification('The Device was successfully removed.', 'success', true)
                componentConfigData.value = data
                window.location.reload()
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString())
                return false
            })
            .finally(() => {
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (deviceType, taskDesc) => {
        // just set the state and the modal will appear
        confirmDeviceType.value = deviceType
        confirmTaskDesc.value = taskDesc
        showDeleteModal.value = true
    }

    function NewDeviceDropdown() {
        if (manifest.value && manifest.value['device-configs'].length) {
                let deviceOptions = manifest.value['device-configs'].map(s => {
                    let escapedName = encodeURIComponent(s.name)
                return (
                    <Dropdown.Item href={`/be/${beId}/utgs/${componentId}/devices/${escapedName}`} key={s.name}>{s.name}</Dropdown.Item>
                )
            })

            return (
                <DropdownButton id="dropdown-add-device"
                    variant="outline-secondary"
                    title="New Device"
                    className="mx-3">
                    {deviceOptions}
                </DropdownButton>
            )
        }
    }

    if (utgLoading.value || componentConfigLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        {canEditSignal.value && <ConfirmationModal show={showDeleteModal.value} handleCancel={() => showDeleteModal.value = false} handleConfirm={() => deleteDevice()} message={`Are you sure you want to delete ${confirmTaskDesc.value}? This action cannot be undone.`} title={`Deleting ${confirmTaskDesc.value}`} />}
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                UTG List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='UTG Devices' canEditSignal={canEditSignal} />
            <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
        </Stack>
        <UtgNav beId={beId} componentId={componentId} activeKey="3"></UtgNav>
        <Table responsive striped hover size="sm">
            <thead>
                <tr>
                    <th>Device Type</th>
                    <th>Device Description</th>
                    <th>Thread Type</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <DeviceTableRows devices={devices.value} />
        </Table>
        {canEditSignal.value && <NewDeviceDropdown />}
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}